<template>
	<div class="settings-button" @click.prevent="openMenu($event)">
		<button type="button" class="navbar__button__link" :disabled="loading">
			<FontAwesomeIcon style="margin: 0; margin-inline-start: 3px; width: 20px; font-size: 16px" :icon="faBars" />
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { faBars } from '@fortawesome/free-solid-svg-icons';

import PagesList from '@/components/nav/static-pages/PagesList.vue';
import { COUNTRY_EXCEPTIONS, WEB_LOCALES } from '@/constants/web-locales.constant';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { StaticPage, fetchStaticPages } from '@/helpers/static-page-helper';
import { getNavItemVisibility } from '@/helpers/navigation-helper';
import { sportTranslated } from '@/features/sports/types/enums';
import type { SportLocale } from '@/features/sports/types/enums';
import { PopoverHelper } from '@/helpers/vfm/popover-helper';

import type { WebLocale } from '@/enums/web-locale';
import type { PageListItem } from '@/components/nav/static-pages/types';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { buildUrlForDomain, Domain } from '@/routing/helpers/subdomain-helper';

const { t } = useI18n();
const router = useRouter();
const { country, language, webLocale } = storeToRefs(useLanguageStore());
const { activeDomain } = storeToRefs(useRoutingStore());

const loading = ref<boolean>(false);
const staticPages = ref<StaticPage[]>([]);

async function fetchSettingItems(): Promise<void> {
	const countryLocal: WebLocale = country.value.toLowerCase() as WebLocale;
	const locale = WEB_LOCALES[countryLocal] || WEB_LOCALES[COUNTRY_EXCEPTIONS[countryLocal]];

	staticPages.value = await fetchStaticPages(locale, { language: language.value });
}

const formattedPagesDetails = computed<PageListItem[]>(() => {
	const conditionalLinks: PageListItem[] = [];

	// Add Sports link when JWTV is available
	const sportsVisibility = getNavItemVisibility(webLocale.value, 'sports', 'settings');
	if (sportsVisibility.mobile || sportsVisibility.desktop) {
		const sportsTranslation = sportTranslated[webLocale.value as SportLocale];
		if (sportsTranslation) {
			conditionalLinks.push({
				fullPath:
					activeDomain.value !== Domain.SPORTS
						? buildUrlForDomain(`/${webLocale.value}`, Domain.SPORTS)
						: `/${webLocale.value}`,
				external: activeDomain.value !== Domain.SPORTS,
				label: t('WEBAPP_SPORTS'),
				openInNewTab: false,
			});
		}
	}

	// Add Guide link for mobile when both JWTV and Guide are available
	const guideVisibility = getNavItemVisibility(webLocale.value, 'guide', 'settings');
	if (guideVisibility.mobile) {
		const path =
			activeDomain.value !== Domain.EDITORIAL
				? buildUrlForDomain(`/${webLocale.value}`, Domain.EDITORIAL)
				: `/${webLocale.value}`;
		conditionalLinks.push({
			fullPath: path,
			external: activeDomain.value !== Domain.EDITORIAL,
			label: t('WEBAPP_GUIDE'),
			openInNewTab: false,
		});
	}

	const staticPagesConfigs = staticPages.value
		.filter(page => !['STATIC_FAQ', 'STATIC_TERMS', 'STATIC_PRIVACY_POLICY'].includes(page.technical_name))
		.map(page => {
			const { full_path, translation, technical_name } = page;

			// About us now lives on the group site instead of the in-app static page
			if (technical_name === 'STATIC_ABOUT') {
				return {
					fullPath: 'https://group.justwatch.com/',
					label: translation,
					external: true,
					openInNewTab: true,
				};
			}

			return {
				fullPath: full_path,
				label: translation,
				external: false,
				openInNewTab: false,
			};
		});

	staticPagesConfigs.splice(3, 0, {
		fullPath: 'https://group.justwatch.com/media/',
		external: true,
		label: t('WEBAPP_JUSTWATCHMEDIA'),
		openInNewTab: true,
	});

	return [
		...conditionalLinks,
		...staticPagesConfigs,
		{
			fullPath: `/${webLocale.value}/faq`,
			external: false,
			label: t('WEBAPP_STATIC_FAQ'),
		},
		{
			fullPath: `/${webLocale.value}/terms`,
			external: false,
			label: t('WEBAPP_PAGE_NAME_TRANSLATABLE_STATIC_TERMS'),
		},
		{
			fullPath: `/${webLocale.value}/privacy-policy`,
			external: false,
			label: t('WEBAPP_STATIC_PRIVACY_POLICY'),
		},
	];
});

async function openMenu(event: Event) {
	// Store reference to the element before any async operations
	// Event.currentTarget becomes null after await due to event pooling
	const targetElement = event.currentTarget as HTMLElement;

	loading.value = true;
	TrackingHelper.trackEvent('userinteraction', {
		action: 'open_dropdown_menu',
	});

	if (!staticPages.value.length) {
		await fetchSettingItems();
	}

	PopoverHelper.create({
		popoverProps: {
			cssClass: 'static-pages-popover',
			boundingElement: targetElement,
			position: 'left',
		},
		component: PagesList,
		componentProps: {
			pages: formattedPagesDetails.value,
			activeDomain: activeDomain.value,
			router,
		},
		onOpened: () => {
			loading.value = false;
		},
	});
}

watch(language, () => {
	fetchSettingItems();
});
</script>

<style lang="scss" scoped>
.navbar__button__link {
	background: none;
	border: none;
	outline: none;
	cursor: pointer;
}
</style>
